import {
  Client,
  participantForms,
  TriStateFilterValue,
  SEARCH_MIN_LENGTH,
  FILTER_DEBOUNCE_MS,
} from '@entities/clients';
import { useSminexClubSearch, clearSminexClubSearch, SminexClubSearchClient } from '@shared/api/sminex-club-search-ws';
import { concatArray, pluralizePhrase } from '@shared/lib';
import { useFetchDeleteTicket } from '@features/api/use-delete-tickets';
import { Button, Checkbox, PreloaderCircular } from '@shared/ui/kit';
import { useFiltersProjects } from '@shared/api/filters-ws';
import styles from './add-clients-to-event-modal.module.scss';
import { useCreateTickets } from '@shared/api/events';
import InputSearch from '@shared/ui/input-search';
import { dealsSummMillionsToRubles, EMPTY_DEALS_SUMM, isDealsSummRangeValid } from './FilterClients/dealsSumm';
import { FilterClients } from './FilterClients';
import { useParams } from 'react-router-dom';
import { useEffect, useRef, useState } from 'react';
import { Modal } from '@shared/ui/modal';
import { SelectValue } from '@shared/ui/select';
import { useToast } from '@shared/hooks/useToast';
import clsx from 'clsx';

interface AddClientsToEventModalProps {
  isOpen: boolean;
  onClose: () => void;
  availableClients: Client[];
  refetchClients: () => void;
}

const excludeByCrmUuids = (
  selected: SminexClubSearchClient[],
  crmUuids: Array<string | null | undefined>
): SminexClubSearchClient[] => selected.filter((item) => !crmUuids.includes(item.crmUuid));

export const AddClientsToEventModal = ({
  isOpen,
  onClose,
  availableClients,
  refetchClients,
}: AddClientsToEventModalProps) => {
  const [selectedClients, setSelectedClients] = useState<SminexClubSearchClient[]>([]);
  const [searchValue, setSearchValue] = useState('');
  const [projectCodes, setProjectCodes] = useState<SelectValue[]>([]);
  const [dealsSumm, setDealsSumm] = useState(EMPTY_DEALS_SUMM);
  const [hasBroker, setHasBroker] = useState<TriStateFilterValue>(null);
  const [doNotCall, setDoNotCall] = useState<TriStateFilterValue>(null);

  const [hasSearched, setHasSearched] = useState(false);
  const [hasInteracted, setHasInteracted] = useState(false);
  const clientsScrollRef = useRef<HTMLDivElement>(null);
  const isOpenRef = useRef(isOpen);
  isOpenRef.current = isOpen;
  const isFreshSearchRef = useRef(false);
  const loadMoreTriggerRef = useRef<HTMLDivElement>(null);

  const { clients, status, hasMore, isLoadingMore, loadMoreError, search, cancel, loadMore, retryLoadMore } =
    useSminexClubSearch();
  const { projects, isLoading: isFiltersLoading } = useFiltersProjects({ requestIfEmpty: isOpen });
  const { deleteTickets } = useFetchDeleteTicket();
  const { createTickets } = useCreateTickets();
  const { showToast } = useToast();
  const { id } = useParams();

  const isSearching = status === 'connecting';
  const isReady = status === 'ready';
  const isIndeterminate = clients.some(({ crmUuid }) => selectedClients.some((item) => item.crmUuid === crmUuid));
  const isAllChecked =
    !!clients.length && clients.every(({ crmUuid }) => selectedClients.some((item) => item.crmUuid === crmUuid));
  const showFiltersLoader = isOpen && isFiltersLoading;
  const hasClients = isReady && clients.length > 0;
  const showPlaceholder = !hasSearched && !isSearching;
  const showNotFound = hasSearched && isReady && clients.length === 0;
  const showSelectedCounter = selectedClients.length > 0;
  const checkAllClassName = clsx(styles.client, !isAllChecked && isIndeterminate && styles.checkAllButton);
  const clientsStateClassName = clsx(styles.clients, (showPlaceholder || showNotFound) && styles.clientsBordered);

  const onSearchValueChange = (value: string) => {
    setHasInteracted(true);
    setSearchValue(value);
  };

  const onProjectCodesChange = (value: SelectValue[]) => {
    setHasInteracted(true);
    setProjectCodes(value);
  };

  const onDealsSummChange = (value: typeof dealsSumm) => {
    setHasInteracted(true);
    setDealsSumm(value);
  };

  const onHasBrokerChange = (value: TriStateFilterValue) => {
    setHasInteracted(true);
    setHasBroker(value);
  };

  const onDoNotCallChange = (value: TriStateFilterValue) => {
    setHasInteracted(true);
    setDoNotCall(value);
  };

  const onCheckboxClick = (client: SminexClubSearchClient) => {
    const isChecked = selectedClients.some(({ crmUuid }) => crmUuid === client.crmUuid);

    if (isChecked) {
      setSelectedClients((prev) => prev.filter(({ crmUuid }) => crmUuid !== client.crmUuid));
    } else {
      setSelectedClients((prev) => [...prev, client]);
    }
  };

  const onCheckAllClick = () => {
    if (isAllChecked) {
      const crmUuids = clients.map(({ crmUuid }) => crmUuid);
      setSelectedClients((prev) => excludeByCrmUuids(prev, crmUuids));
      return;
    }

    const newSelection = structuredClone(selectedClients);
    clients.forEach(({ crmUuid }) => newSelection.filter((item) => item.crmUuid === crmUuid));
    setSelectedClients(clients);
  };

  const onSaveClients = async () => {
    try {
      let newClients = structuredClone(selectedClients);
      const ticketIdsToDelete = [];

      for (const client of availableClients) {
        newClients = newClients.filter(({ crmUuid }) => crmUuid !== client.crmUuid);
        if (client.crmUuid && selectedClients.some(({ crmUuid }) => crmUuid === client.crmUuid)) continue;
        ticketIdsToDelete.push(client.ticket.id);
      }

      if (newClients.length) {
        const guestsData = newClients.map(({ lastName, firstName, surname, crmUuid }) => ({
          lastName,
          firstName,
          surname,
          uuid: crmUuid,
        }));

        const { error, data } = await createTickets({ guestsData, eventId: Number(id), source: 'SminexClub' });

        if (error || data?.createTicket.errorsData.length) {
          showToast('Ошибка сохранения данных', 'error');
          return;
        }
      }

      if (ticketIdsToDelete.length) {
        await deleteTickets({ ticketIds: ticketIdsToDelete });
      }

      refetchClients();
      onClose();
    } catch {
      showToast('Ошибка сохранения данных', 'error');
    }
  };

  useEffect(() => {
    return () => {
      clearSminexClubSearch();
    };
  }, []);

  useEffect(() => {
    if (!hasInteracted) return;

    const trimmedSearch = searchValue.trim();
    const isSearchEligible = !searchValue.length || trimmedSearch.length >= SEARCH_MIN_LENGTH;
    if (!isSearchEligible) return;
    if (!isDealsSummRangeValid(dealsSumm)) return;

    const timer = setTimeout(() => {
      if (!isOpenRef.current) return;

      setHasSearched(true);
      isFreshSearchRef.current = true;
      search({
        searchString: trimmedSearch,
        projectCodes: projectCodes.map(String),
        isBroker: hasBroker,
        callRefusal: doNotCall,
        dealsSummFrom: dealsSummMillionsToRubles(dealsSumm.from),
        dealsSummTo: dealsSummMillionsToRubles(dealsSumm.to),
      });
    }, FILTER_DEBOUNCE_MS);

    return () => clearTimeout(timer);
  }, [hasInteracted, searchValue, projectCodes, dealsSumm, hasBroker, doNotCall]);

  useEffect(() => {
    if (isOpen || (status !== 'connecting' && !isLoadingMore)) return;
    isFreshSearchRef.current = false;
    cancel();
  }, [isOpen]);

  useEffect(() => {
    if (status !== 'error') return;
    isFreshSearchRef.current = false;
    showToast('Ошибка загрузки данных', 'error');
  }, [status]);

  useEffect(() => {
    if (!loadMoreError) return;
    showToast('Ошибка загрузки данных', 'error');
  }, [loadMoreError]);

  useEffect(() => {
    if (!isFreshSearchRef.current || !clientsScrollRef.current) return;
    clientsScrollRef.current.scrollTop = 0;
    isFreshSearchRef.current = false;
  }, [clients]);

  useEffect(() => {
    const node = loadMoreTriggerRef.current;
    if (!node || isLoadingMore) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) {
          loadMore();
        }
      },
      { root: clientsScrollRef.current, rootMargin: '200px' }
    );

    observer.observe(node);

    return () => observer.disconnect();
  }, [hasMore, loadMoreError, isLoadingMore, loadMore]);

  useEffect(() => {
    if (!isOpen) return;

    const items = availableClients
      .filter(({ crmUuid }) => crmUuid)
      .map(({ crmUuid, firstName, lastName, surname }) => ({
        crmUuid,
        firstName,
        lastName,
        surname,
        manager: null,
        broker: null,
        callRefusal: null,
        projects: [],
      }));

    setSelectedClients(items);
  }, [isOpen]);

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Добавить участников"
      width={920}
      className={styles.modal}
      cancelButton={{ text: 'Отменить', onClick: onClose, settings: { width: '106px', minWidth: '106px' } }}
      applyButton={{
        text: 'Добавить выбранных',
        onClick: onSaveClients,
        isDisabled: showFiltersLoader || selectedClients.length === 0 || isSearching,
        settings: { width: '186px', minWidth: '186px' },
      }}
      headerClassName={styles.modalHeader}
    >
      <div className={styles.container}>
        {showFiltersLoader ? (
          <div className={styles.contentLoader}>
            <PreloaderCircular />
          </div>
        ) : (
          <div className={styles.content}>
            <FilterClients
              projects={projects}
              projectCodes={projectCodes}
              onProjectCodesChange={onProjectCodesChange}
              dealsSumm={dealsSumm}
              onDealsSummChange={onDealsSummChange}
              hasBroker={hasBroker}
              onHasBrokerChange={onHasBrokerChange}
              doNotCall={doNotCall}
              onDoNotCallChange={onDoNotCallChange}
              isDisabled={isSearching}
            />

            <div className={styles.clientsContainer}>
              <div className={styles.actions}>
                <InputSearch
                  searchValue={searchValue}
                  changeValue={onSearchValueChange}
                  clearHandler={() => onSearchValueChange('')}
                  isDisabled={isSearching}
                />
              </div>

              {hasClients ? (
                <div className={styles.clientsListSection}>
                  <div className={styles.clientsListHeader}>
                    <div className={checkAllClassName}>
                      <Checkbox
                        size="small"
                        onChange={onCheckAllClick}
                        isChecked={isAllChecked}
                        isIndeterminate={!isAllChecked && isIndeterminate}
                      />
                      Выбрать всех
                      {showSelectedCounter && (
                        <span className={styles.clientsCounter}>
                          Выбрано: {selectedClients.length} {pluralizePhrase(selectedClients.length, participantForms)}
                        </span>
                      )}
                    </div>
                    <div className={styles.clientsSeparator} />
                  </div>

                  <div ref={clientsScrollRef} className={styles.clients}>
                    {clients.map((client) => (
                      <div key={client.crmUuid} className={styles.client}>
                        <Checkbox
                          size="small"
                          onChange={() => onCheckboxClick(client)}
                          isChecked={selectedClients.some(({ crmUuid }) => crmUuid === client.crmUuid)}
                        />

                        <span>{concatArray([client.lastName, client.firstName, client.surname])}</span>
                      </div>
                    ))}

                    {hasMore && !loadMoreError && <div ref={loadMoreTriggerRef} />}

                    {isLoadingMore && (
                      <div className={styles.loadingMore}>
                        <PreloaderCircular />
                      </div>
                    )}

                    {loadMoreError && (
                      <div className={styles.loadingMore}>
                        <Button variant="secondary" size="small" onClick={retryLoadMore}>
                          Повторить
                        </Button>
                      </div>
                    )}
                  </div>
                </div>
              ) : (
                <div className={clientsStateClassName}>
                  {isSearching && (
                    <div className={styles.loader}>
                      <PreloaderCircular />
                    </div>
                  )}

                  {showPlaceholder && (
                    <span className={styles.notFound}>
                      Установите необходимые настройки в фильтре слева, <br /> чтобы получить список клиентов
                    </span>
                  )}

                  {showNotFound && <span className={styles.notFound}>Ничего не найдено</span>}
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </Modal>
  );
};
